import { VALIDATION } from './TransportCloudForm.consts';
import { currencyCode, FormInputs } from './types';

const hasTruckersRateValue = (truckersRate: number | null): boolean =>
  truckersRate !== null && truckersRate !== undefined && truckersRate > 0;

const hasCurrencyValue = (code: currencyCode | null): boolean =>
  code !== null && code !== undefined;

const getCRC = (value: string): number => {
  const map = [
    10, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 23, 24, 25, 26, 27, 28, 29, 30,
    31, 32, 34, 35, 36, 37, 38,
  ];
  const weights = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512];
  const digits: number[] = [];
  let total = 0;
  let check = 0;

  for (let i = 0; i < 4; i++) {
    if (value[i] && map[value[i].charCodeAt(0) - 'A'.charCodeAt(0)]) {
      digits[i] = map[value[i].charCodeAt(0) - 'A'.charCodeAt(0)];
    } else {
      return -1;
    }
  }

  for (let i = 4; i < 10; i++) {
    digits[i] = parseInt(value[i], 10);
  }

  for (let i = 0; i < 10; i++) {
    digits[i] *= weights[i];
  }

  for (let i = 0; i < 10; i++) {
    total += digits[i];
  }

  check = total % 11;

  if (check === 10) {
    check = 0;
  }

  return check;
};

const isISO6346Valid = (value: string | null): boolean => {
  if (!value) {
    return true;
  }

  if (value.length !== 11) {
    return false;
  }
  return +value[value.length - 1] === getCRC(value);
};

export const validateISO6346 = (containerNumber: string | null): boolean =>
  isISO6346Valid(containerNumber);

export const validateCurrencyCode = (
  currency: currencyCode | null,
  truckersRate: number | null,
): string | undefined => {
  if (hasTruckersRateValue(truckersRate) && !hasCurrencyValue(currency)) {
    return VALIDATION.currency;
  }
  return undefined;
};

export const validateTruckersRate = (
  truckersRate: number | null,
  currency: currencyCode | null,
): string | undefined => {
  if (hasCurrencyValue(currency)) {
    if (!hasTruckersRateValue(truckersRate)) {
      return VALIDATION.truckersRate;
    }
  }
  return undefined;
};

export const getCurrencyCodeValidationRules = (
  getValues: () => FormInputs,
) => ({
  validate: {
    requiredWhenTruckersRate: (value: currencyCode | null) => {
      const { truckersRate } = getValues();
      return validateCurrencyCode(value, truckersRate);
    },
  },
});

export const getTruckersRateValidationRules = (
  getValues: () => FormInputs,
) => ({
  min: { value: 0, message: VALIDATION.fieldMinZero },
  validate: {
    requiredWhenCurrency: (value: number | null) => {
      const { currencyCode: formCurrencyCode } = getValues();
      return validateTruckersRate(value, formCurrencyCode);
    },
  },
});

export const getContainerNumbersValidationRules = (
  getValues: () => FormInputs,
) => ({
  validate: {
    amountLimit: (value: string[]) => {
      const { amount } = getValues();
      if (
        amount &&
        amount > 0 &&
        value &&
        value.length > 0 &&
        value.length > amount
      ) {
        return VALIDATION.containerNumbersAmount;
      }
      return undefined;
    },
    iso6346Format: (value: string[]) => {
      if (!value || value.length === 0) return undefined;

      for (const containerNumber of value) {
        if (!validateISO6346(containerNumber)) {
          return VALIDATION.containerNumbersISO;
        }
      }
      return undefined;
    },
  },
});
